import axiosInstance from '@helpers/Axios';
import { logError } from '@utils/axios/ErrorLogging';
import ProfileRequestAccessVO, { GuestDetailsVO } from '@interfaces/profiles/ProfileRequestAccessVO';
import { getAnonymousViewerId, setAnonymousViewerId, setPublicUserData } from '@utils/profile/ProfileAccessManagementUtils';
import { toVerifyAccessMetadata } from '@utils/profile/GuestFormFieldUtils';

const errorLogger = (e: unknown, method: string) => {
  logError(e, 'ProfileAccessManagementService.ts', method);
};

export type SendAccessRequestOtpResponse = {
  otpRequired?: boolean;
  no_of_attemps?: string;
  additional_message?: string;
  message?: string;
  error?: string;
  error_description?: string;
  /** Present when API returns error G101 with additional_message ACCESS_DENIED */
  accessDenied?: boolean;
  [k: string]: any;
};

/** Response shape from POST .../send-access-request-otp when access is denied */
export const isSendAccessRequestOtpAccessDeniedPayload = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const d = data as Record<string, unknown>;
  return d.additional_message === 'ACCESS_DENIED' && d.error === 'G101';
};

export const sendAccessRequestOtp = async (
  companyId: string,
  profileId: string,
  mailId: string,
  recaptchaToken: string | undefined,
  recaptchaAction: string | undefined,
  callBackFunc: (response: SendAccessRequestOtpResponse) => void,
  viewId?: string
) => {
  try {
    const data = {
      mailId,
      anonymousViewer: getAnonymousViewerId(),
      recaptchaToken,
      recaptchaAction,
      ...(viewId && { viewId })
    };
    const response = await axiosInstance().post(`/guest_v3/public/${companyId}/profiles/${profileId}/send-access-request-otp`, data);
    if (!response) {
      return;
    }
    const body = (response.data || {}) as SendAccessRequestOtpResponse;
    if (response.status === 200) {
      if (isSendAccessRequestOtpAccessDeniedPayload(body)) {
        callBackFunc({ ...body, accessDenied: true });
        return;
      }
      callBackFunc(body);
      return;
    }
    if (response.status === 400 && isSendAccessRequestOtpAccessDeniedPayload(body)) {
      callBackFunc({ ...body, accessDenied: true });
    }
  } catch (e) {
    errorLogger(e, 'sendAccessRequestOtp');
  }
};

export const verifyProfileAccessRequest = async (
  companyId: string,
  profileId: string,
  userName: string,
  ndaOrigin: boolean,
  isFromSubscribeBtn: boolean,
  fromAsk: boolean,
  otp: string | undefined,
  callBackFunc: (requestAccess: ProfileRequestAccessVO) => void,
  viewId?: string,
  guestDetails?: Partial<GuestDetailsVO>,
  skipOtp?: string
) => {
  try {
    const metadata = toVerifyAccessMetadata(guestDetails);
    const data = {
      mailId: userName,
      ndaOrigin,
      subscribeOrigin: isFromSubscribeBtn,
      fromAsk,
      anonymousViewer: getAnonymousViewerId(),
      ...(skipOtp && { skipOtp }),
      ...(otp && { otp }),
      ...(viewId && { viewId }),
      ...(guestDetails?.productCategory?.length ? { productCategory: guestDetails.productCategory } : {}),
      ...(Object.keys(metadata).length > 0 && { metadata })
    };
    const response = await axiosInstance().post(`/guest_v3/public/${companyId}/profiles/${profileId}/verify-profile-access`, data);
    if (response && response.status === 200 && response.data) {
      if (response.data.additionalParams?.anonymousViewer && data.anonymousViewer !== response.data.additionalParams.anonymousViewer) {
        setAnonymousViewerId(response.data.additionalParams.anonymousViewer, true);
      }
      callBackFunc(response.data);
    } else if (response && response.status === 400 && response.data) {
      // Caller decides how to handle (e.g., invalid OTP, access denied, etc.)
      callBackFunc(response.data);
    }
  } catch (e) {
    errorLogger(e, 'verifyProfileAccessRequest');
    callBackFunc(null as unknown as ProfileRequestAccessVO);
  }
};

export const reclaimProfileAccessRequest = async (myObject: Record<string, unknown>, callBackFunc: (requestAccess: ProfileRequestAccessVO) => void) => {
  try {
    const response = await axiosInstance().post('/guest_v3/profiles/reclaim-profile-access', myObject);
    if (response && response.status === 200) {
      callBackFunc(response.data);
    }
  } catch (e) {
    errorLogger(e, 'reclaimProfileAccessRequest');
  }
};

const storeGuestSession = (userData: Record<string, unknown>, guestKey: string): void => {
  const sessionData = { ...userData, guestKey };
  const companyGuestVO = sessionData.companyGuestVO as { id?: string } | undefined;
  const key = companyGuestVO?.id ?? 'guestUserData';
  localStorage.setItem('guestUserData', JSON.stringify(sessionData));
  localStorage.setItem(key, JSON.stringify(sessionData));
  if (key !== 'guestUserData') {
    sessionStorage.setItem('profileId', key);
  }
};

/** After access-request OTP + verify-profile-access, establish auth without a second send-otp modal. */
export const establishGuestSessionAfterAccessRequestOtp = async (profileId: string, mailId: string, guestKey: string, otp: string): Promise<boolean> => {
  const persistPublicUserOtpVerified = () => {
    setPublicUserData(profileId, mailId, { otp_verified: true, verified_date: new Date() });
  };

  try {
    const initResponse = await axiosInstance().get(`/guest_v3/init/${guestKey}`, { params: { module: 'profile', sendOtp: false } });
    if (initResponse?.status === 200 && initResponse.data?.companyGuestResponseVO) {
      storeGuestSession(initResponse.data.companyGuestResponseVO, guestKey);
      persistPublicUserOtpVerified();
      return true;
    }

    const validateResponse = await axiosInstance().post('/guest_v3/validate-otp', { otp, guestKey, module: 'profiles' });
    if (validateResponse?.status === 200 && validateResponse.data) {
      storeGuestSession(validateResponse.data, guestKey);
      persistPublicUserOtpVerified();
      return true;
    }
  } catch (e) {
    errorLogger(e, 'establishGuestSessionAfterAccessRequestOtp');
  }
  return false;
};
